/**
 * PhotoFiltersBar Component
 * 
 * Horizontal filter pills for photo gallery.
 */

import React from "react";
import { View, Text, ScrollView, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import type { PhotoFilters, PhotoActivityType } from "../types";

interface FilterOption {
  key: string;
  label: string;
  icon?: string;
}

interface PhotoFiltersBarProps {
  activeFilter: string;
  onFilterChange: (filter: string) => void;
  weekendName?: string;
  showActivityTypes?: boolean;
}

const FILTER_OPTIONS: FilterOption[] = [
  { key: "all", label: "Alles", icon: "images" },
  { key: "featured", label: "Uitgelicht", icon: "star" },
  { key: "my", label: "Mijn foto's", icon: "person" },
  { key: "team", label: "Mijn team", icon: "people" },
];

const ACTIVITY_OPTIONS: FilterOption[] = [
  { key: "general", label: "Algemeen", icon: "camera" },
  { key: "challenge", label: "Opdrachten", icon: "flag" },
  { key: "speurtocht", label: "Speurtocht", icon: "map" },
  { key: "agenda_event", label: "Evenementen", icon: "calendar" },
];

/**
 * Filter bar component
 */
export const PhotoFiltersBar: React.FC<PhotoFiltersBarProps> = ({
  activeFilter,
  onFilterChange,
  weekendName,
  showActivityTypes = false,
}) => {
  const colors = useThemeColors();

  const options = showActivityTypes 
    ? [...FILTER_OPTIONS, ...ACTIVITY_OPTIONS]
    : FILTER_OPTIONS;

  return (
    <View
      className="border-b"
      style={{ borderBottomColor: colors.border }}
    >
      {/* Weekend indicator */}
      {weekendName && (
        <View className="px-4 py-2 flex-row items-center">
          <Ionicons name="calendar" size={16} color={colors.primary} />
          <Text
            className="ml-2 text-sm font-medium"
            style={{ color: colors.primary }}
          >
            {weekendName}
          </Text>
        </View>
      )}

      {/* Filter pills */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 12, paddingVertical: 8 }}
      >
        {options.map((option) => {
          const isActive = activeFilter === option.key;
          return (
            <TouchableOpacity
              key={option.key}
              onPress={() => onFilterChange(option.key)}
              className="flex-row items-center px-3 py-1.5 rounded-full mr-2"
              style={{
                backgroundColor: isActive ? colors.primary : colors.surfaceSecondary,
              }}
              accessibilityRole="button"
              accessibilityLabel={option.label}
              accessibilityState={{ selected: isActive }}
            >
              {option.icon && (
                <Ionicons
                  name={option.icon as any}
                  size={14}
                  color={isActive ? "#FFFFFF" : colors.textSecondary}
                />
              )}
              <Text
                className="ml-1.5 text-sm font-medium"
                style={{
                  color: isActive ? "#FFFFFF" : colors.textSecondary,
                }}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
};

export default PhotoFiltersBar;

